import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query and return whether it currently matches.
 *
 * Mirrors the matchMedia pattern already used inline in LeftRail (the rail
 * auto-collapse): evaluate once on mount, then update only when the breakpoint is
 * crossed. Used by the genuinely-wide layout flags (#131, re-tuned #192 — the
 * SITREP dock and the command-bar left-anchor both gate on WIDE_DISPLAY_QUERY,
 * see lib/wideDisplay.ts).
 *
 * SSR/test-safe: returns false when `window.matchMedia` is unavailable.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const apply = (): void => setMatches(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [query]);

  return matches;
}
